import React, { useEffect, useRef, useState } from "react";
import { Layout, Drawer, Grid } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import "../Styles/Sidebar.scss";
import { CircleHelp, Menu } from "lucide-react";
import { useLanguage } from "src/hooks/UseLanguage";
import { DASHBOARD_APP_BASE } from "../Config/DashboardRoutes";
import {
  HELP_SUPPORT_ROUTE,
  buildSidebarSections,
  type SidebarItem,
  type SidebarSectionConfig,
} from "../Config/SidebarConfig";
import { useDashboardSidebarItems } from "../Hooks/UseDashboardSidebarItems";
import SidebarSection, { type SidebarMode } from "./SidebarSection";

const { Sider } = Layout;

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { isRTL } = useLanguage();
  // Collapsed-rail tooltips and the mobile drawer sit against the sidebar's
  // outer edge, which swaps sides with the writing direction.
  const tooltipPlacement = isRTL ? "left" : "right";
  const drawerPlacement = isRTL ? "right" : "left";

  const [mode, setMode] = useState<SidebarMode>("collapsed");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [openSubMenus, setOpenSubMenus] = useState<string[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const dashboardItems = useDashboardSidebarItems();

  const sections = buildSidebarSections(t, dashboardItems);
  const effectiveMode: SidebarMode = isMobile ? "open" : mode;

  useEffect(() => {
    if (isMobile) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        mode === "open" &&
        sidebarRef.current &&
        !sidebarRef.current.contains(e.target as Node)
      ) {
        setMode("collapsed");
        setOpenSections([]);
        setOpenSubMenus([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mode, isMobile]);

  const closeDrawer = () => {
    setDrawerVisible(false);
    setOpenSections([]);
    setOpenSubMenus([]);
  };

  const toggleSection = (key: string) => {
    setOpenSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const toggleSubMenu = (name: string) => {
    setOpenSubMenus(prev => (prev.includes(name) ? prev.filter(n => n !== name) : [name]));
  };

  const handleSectionClick = (section: SidebarSectionConfig) => {
    if (!isMobile && effectiveMode === "collapsed") {
      setMode("open");
      setOpenSections([section.key]);
      return;
    }

    if (section.items && section.items.length > 0) {
      toggleSection(section.key);
      setActiveSection(null);
    } else if (section.route) {
      navigate(section.route);
      setActiveSection(section.key);
      setActiveItem(null);
      if (isMobile) {
        closeDrawer();
      }
    }
  };

  const handleSectionHover = () => {
    if (!isMobile && effectiveMode === "collapsed") {
      setMode("open");
    }
  };

  const handleItemClick = (item: SidebarItem) => {
    if (!item.route) {
      return;
    }
    navigate(item.route);
    setActiveItem(item.name);
    setActiveSection(null);
    if (isMobile) {
      closeDrawer();
    }
  };

  const handleLogoClick = () => {
    const firstDashboardItem = sections.find(section => section.key === "dashboards")?.items?.[0];
    navigate(firstDashboardItem?.route ?? DASHBOARD_APP_BASE);
    if (firstDashboardItem?.name) {
      setActiveSection("dashboards");
      setActiveItem(firstDashboardItem.name);
    }
  };

  const handleHelpSupportClick = () => {
    navigate(HELP_SUPPORT_ROUTE);
    setActiveSection("helpAndSupport");
    setActiveItem(null);
    setOpenSections([]);
    setOpenSubMenus([]);
    if (isMobile) {
      closeDrawer();
    }
  };

  const logoSize = mode === "collapsed" ? "30" : "40";

  const renderSidebarContent = () => (
    <div className={`sidebar-inner ${effectiveMode}`}>
      <div className="sidebar-logo">
        <div className="demo-logo-vertical" onClick={handleLogoClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={logoSize}
            height={logoSize}
            viewBox="0 0 35 32"
            fill="none"
          >
            <path
              d="M1 30.5C12.2113 27.974 5.52192 2.0716 17 1.5C28.5918 0.922731 22.2352 27.7056 33.5 30.5"
              stroke="white"
              strokeWidth="3"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>

      <div className="sidebar-sections">
        {sections.map(section => (
          <SidebarSection
            key={section.key}
            section={section}
            mode={effectiveMode}
            tooltipPlacement={tooltipPlacement}
            isActive={activeSection === section.key}
            isOpen={openSections.includes(section.key)}
            activeItem={activeItem}
            openSubMenus={openSubMenus}
            onHeaderClick={handleSectionClick}
            onHeaderHover={handleSectionHover}
            onItemClick={handleItemClick}
            onToggleSubMenu={toggleSubMenu}
          />
        ))}
      </div>

      <Tippy
        disabled={effectiveMode === "open"}
        content={t("sidebar.sections.helpAndSupport")}
        placement={tooltipPlacement}
      >
        <div
          className={`sidebar-footer ${effectiveMode} ${activeSection === "helpAndSupport" ? "section-active" : ""}`}
          onClick={handleHelpSupportClick}
        >
          {effectiveMode === "open" && (
            <span className="label">{t("sidebar.sections.helpAndSupport")}</span>
          )}
          <span className="sidebar-footer__icon">
            <CircleHelp size={18} />
          </span>
        </div>
      </Tippy>
    </div>
  );

  return (
    <>
      {isMobile && (
        <button
          className="sidebar-hamburger"
          onClick={() => setDrawerVisible(true)}
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
      )}
      {isMobile ? (
        <Drawer
          placement={drawerPlacement}
          closable={false}
          onClose={closeDrawer}
          open={drawerVisible}
          styles={{ body: { padding: 0, backgroundColor: "#2c68ad" } }}
          width={280}
        >
          <div className="sidebar sidebar--drawer open">{renderSidebarContent()}</div>
        </Drawer>
      ) : (
        <Sider
          ref={sidebarRef}
          width={effectiveMode === "collapsed" ? 100 : 280}
          className={`sidebar ${effectiveMode}`}
        >
          {renderSidebarContent()}
        </Sider>
      )}
    </>
  );
};

export default Sidebar;
